import { Editor, useEditorState } from '@tiptap/react';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { BlockTypes } from '../../../../models';
import { Select } from '../../../Select';

import classNames from 'classnames';
import classes from './Other.module.scss';

interface OtherProps {
  editor: Editor;
}

export const Other: FunctionComponent<OtherProps> = ({ editor }) => {
  const { t } = useTranslation();

  const {
    isActiveQuote,
    isActiveCallout,
    isActiveCode,
    isActiveDelimiter,
    isActiveParagraph,
    isActiveList,
    isActiveTable,
  } = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isActiveQuote: editor.isActive('quoteNode'),
      isActiveCallout: editor.isActive('calloutNode'),
      isActiveCode: editor.isActive('codeNode'),
      isActiveDelimiter: editor.isActive('delimiterNode'),
      isActiveParagraph: editor.isActive('paragraph'),
      isActiveList:
        editor.isActive('taskList') ||
        editor.isActive('orderedList') ||
        editor.isActive('bulletList'),
      isActiveTable: editor.isActive('table'),
    }),
  });

  const items = useMemo(
    () => [
      { id: BlockTypes.quote, title: t('richTextEditor.tool-names-quote') },
      { id: BlockTypes.callout, title: t('richTextEditor.tool-names-callout') },
      { id: BlockTypes.code, title: t('richTextEditor.tool-names-code') },
      { id: BlockTypes.delimiter, title: t('richTextEditor.tool-names-delimiter') },
      { id: BlockTypes.table, title: t('richTextEditor.tool-names-table') },
    ],
    [t]
  );

  const onChange = useCallback(
    (type: BlockTypes) => {
      switch (type) {
        case BlockTypes.quote:
          return editor.commands.insertQuoteNode({ text: '', caption: '', alignment: 'left' });
        case BlockTypes.callout:
          return editor.commands.insertCalloutNode({ text: '', icon: '' });
        case BlockTypes.code:
          return editor.commands.insertCodeNode({ code: '' });
        case BlockTypes.delimiter:
          return editor.commands.insertDelimiterNode({ style: 'stars' });
        case BlockTypes.table:
          return editor.commands.insertTable({ rows: 2, cols: 3, withHeaderRow: false });
      }
    },
    [editor]
  );

  const selectedItemId = isActiveQuote
    ? BlockTypes.quote
    : isActiveCallout
    ? BlockTypes.callout
    : isActiveCode
    ? BlockTypes.code
    : isActiveDelimiter
    ? BlockTypes.delimiter
    : isActiveTable
    ? BlockTypes.table
    : undefined;

  return (
    <Select
      className={classNames(classes['other'], { [classes['other--mobile']]: isMobileOnly })}
      placeholderLabel={t('common.other')}
      selectedItemId={selectedItemId}
      active={Boolean(selectedItemId)}
      items={items}
      onChange={(type) => onChange(type as BlockTypes)}
      disabled={!isActiveParagraph || isActiveList || isActiveTable}
      {...(isMobileOnly && { compactSidePadding: true, listMinWidthRem: 9.5 })}
      noBorder
    />
  );
};
